<template>
  <!-- Every v2 section sits in a theme band (T1 tokens): background choice,
       alternating rhythm, one heading. Themes restyle it; nothing per theme here. -->
  <section class="t-band s2-frame" :data-variant="variant" :data-background="background" :aria-labelledby="heading ? headingId : null"
           :aria-label="heading ? null : label">
    <div class="t-band__inner" :class="narrow ? 's2-narrow' : ''">
      <p v-if="eyebrow" class="t-eyebrow">{{ eyebrow }}</p>
      <component :is="headingTag" v-if="heading" :id="headingId" class="t-h2 t-band__title mo-reveal" :class="center ? 's2-center' : ''">{{ heading }}</component>
      <slot></slot>
    </div>
  </section>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {computed} from "vue";
import {backgroundOf} from "@/utils/sectionLogic.js";
const headingTag = useSectionHeadingTag();

const props = defineProps({
  section: {type: Object, required: true},
  index: {type: Number, default: 0},
  heading: {type: String, default: ""},
  eyebrow: {type: String, default: ""},
  label: {type: String, default: ""},
  narrow: {type: Boolean, default: false},
  center: {type: Boolean, default: false},
});
const background = computed(() => backgroundOf(props.section));
const variant = computed(() => (props.index % 2 === 0 ? "1" : "2"));
const headingId = computed(() => `h-${String(props.section.id || props.section.type).replace(/[^a-zA-Z0-9_-]/g, "")}`);
</script>

<style>
.s2-frame .t-band__title { margin-top: 0; }
.s2-narrow { max-width: min(var(--t-max-width), 820px); }
.s2-center { text-align: center; }
/* Shared bits the group A sections use. */
.s2-grid { display: grid; gap: var(--t-gap); grid-template-columns: repeat(var(--s2-cols, 3), minmax(0, 1fr)); }
@media (max-width: 860px) { .s2-grid { grid-template-columns: repeat(min(var(--s2-cols, 3), 2), minmax(0, 1fr)); } }
@media (max-width: 560px) { .s2-grid { grid-template-columns: minmax(0, 1fr); } }
.s2-stack > * + * { margin-top: .75rem; }
.s2-muted { color: var(--t-muted); }
.s2-small { font-size: .875rem; }
.s2-input { width: 100%; box-sizing: border-box; min-height: 44px; padding: .65rem .8rem; font: inherit; color: var(--t-ink); background: var(--t-surface);
  border: 1.5px solid color-mix(in srgb, var(--t-ink) 28%, var(--t-line)); border-radius: var(--t-radius-sm); }
.s2-input::placeholder { color: color-mix(in srgb, var(--t-ink) 50%, transparent); }
.s2-input:focus-visible { outline: 3px solid color-mix(in srgb, var(--t-accent) 45%, transparent); outline-offset: 1px; border-color: var(--t-accent); }
.s2-label { display: block; font-weight: 600; margin: 0 0 .35rem; }
.s2-choice { display: flex; align-items: center; justify-content: center; text-align: center; min-height: 48px; padding: .6rem .8rem; cursor: pointer;
  border: 1.5px solid color-mix(in srgb, var(--t-ink) 28%, var(--t-line)); border-radius: var(--t-radius-sm); background: var(--t-surface); color: var(--t-ink); font: inherit; }
.s2-choice[aria-pressed="true"], .s2-choice:has(input:checked) { border-color: var(--t-accent); box-shadow: inset 0 0 0 1px var(--t-accent); background: color-mix(in srgb, var(--t-accent) 9%, var(--t-surface)); }
.s2-choice input { position: absolute; opacity: 0; pointer-events: none; }
.s2-hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }
.s2-ok { padding: 1rem 1.1rem; border-radius: var(--t-radius-sm); background: color-mix(in srgb, #1d9a4a 12%, var(--t-surface)); color: var(--t-ink); }
.s2-err { color: #b42318; font-weight: 600; }
.s2-note { font-size: .82rem; color: var(--t-muted); margin-top: .75rem; }
.s2-studio-gap { border: 2px dashed color-mix(in srgb, var(--t-accent) 60%, transparent); border-radius: var(--t-radius-sm); padding: 1rem; color: var(--t-muted); text-align: center; }
</style>
